import React, {ChangeEvent, useEffect, useRef, useState} from "react";
import {indexWrapCss} from "./css/MyPage.style";
import Header from "../header/Header";
import Footer from "../footer/Footer";
import {ReactComponent as Instar} from 'src/assets/sns/Instagram.svg';
import {ReactComponent as Brunch} from 'src/assets/sns/branch.svg';
import {ReactComponent as Twitter} from 'src/assets/sns/TwitterX.svg';
import {ReactComponent as Blog} from 'src/assets/sns/Internet.svg';
import {ReactComponent as AddSns} from 'src/assets/sns/Add circle.svg';
import {ReactComponent as PlusIcon} from 'src/assets/plus.svg';
import Modal from "react-modal";
import ImageCrop from "../../../domain/author/ImageCrop";
import {useNavigate} from "react-router-dom";
import Alert from "../modal/Alert";
import {updateProfile} from "../../../api/index";
import ProfileExtraFields from "./components/ProfileExtraFields";
import { optionalCharError } from 'src/common/utils/charValidation';

interface Profile {
    id: number;
    authorName: string;
    email: string;
    keyword1: string;
    authorIntro?: string;
    authorDescription?: string;
    instagramLink?: string;
    xlink?: string;
    brunchLink?: string;
    naverLink?: string;
    snsLink1?: string;
    snsLink2?: string;
    profilePhoto?: string;
    mbti?: string;
    writingGenre?: string;
    inspirationSource?: string;
    happiestWritingMoment?: string;
    favoriteWorkAndQuote?: string;
    behindCreationEpisode?: string;
    futureWritingPlan?: string;
}

interface FormErrors {
    name?: string;
    email?: string;
    intro?: string;
    desc?: string;
    bookTitle?: string;
    bookLink?: string;
    profile?: string;
    keyword?: string;
    warning?: string;
    bg?: string;
}

interface ProfileEditProps {
    profileList: Profile;
}

/* ── 섹션 카드 래퍼 ── */
const SectionCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="bg-white rounded-2xl shadow-[0px_1px_4px_rgba(0,0,0,0.08)] border border-[#f3f4f6] px-8 py-8 md:px-5 md:py-6 flex flex-col gap-6">
        <h2 className="text-[18px] font-bold text-[#111827] border-b border-[#f3f4f6] pb-4">{title}</h2>
        {children}
    </div>
);

/* ── 공통 입력 스타일 ── */
const inputBaseCls = "w-full h-12 border border-[#e5e7eb] rounded-xl px-4 text-[15px] text-[#1f2937] placeholder-[#9ca3af] outline-none focus:border-[#19cf7a] transition-all bg-white";
const textareaBaseCls = "w-full border border-[#e5e7eb] rounded-xl px-4 py-3 text-[15px] text-[#1f2937] placeholder-[#9ca3af] outline-none focus:border-[#19cf7a] transition-all resize-none bg-white leading-relaxed";

/* ── 필드 래퍼 ── */
const FieldLabel = ({ label, sub, children }: { label: string; sub?: string; children: React.ReactNode }) => (
    <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-0.5">
            <span className="text-[15px] font-semibold text-[#374151]">{label}</span>
            {sub && <span className="text-[13px] text-[#9ca3af]">{sub}</span>}
        </div>
        {children}
    </div>
);

const ProfileEdit = ({ profileList }: ProfileEditProps) => {
    const navigate = useNavigate();
    const [search, setSearch] = useState<any>([]);
    const onSearchHandler = (e: any) => setSearch(e.target.value);

    const [instarLink, setInstarLink] = useState<string>("");
    const [twitterLink, setTwitterLink] = useState<string>("");
    const [brunchLink, setBrunchLink] = useState<string>("");
    const [blogLink, setBlogLink] = useState<string>("");
    const [snsLink1, setSnsLink1] = useState<string>("");
    const [snsLink2, setSnsLink2] = useState<string>("");

    const [authorIntro, setAuthorIntro] = useState<string>("");
    const [authorDescription, setAuthorDescription] = useState<string>("");

    const introRef = useRef<HTMLTextAreaElement>(null);
    const descRef = useRef<HTMLTextAreaElement>(null);

    const [mbti, setMbti] = useState<string>("");
    const [writingGenres, setWritingGenres] = useState<string[]>([]);
    const [inspireSource, setInspireSource] = useState<string>("");
    const [favoriteWorkTitle, setFavoriteWorkTitle] = useState<string>("");
    const [favoriteWorkReason, setFavoriteWorkReason] = useState<string>("");
    const [memorableEpisode, setMemorableEpisode] = useState<string>("");
    const [futureWritingPlan, setFutureWritingPlan] = useState<string>("");
    const [happiestWritingMoment, setHappiestWritingMoment] = useState<string>("");

    const mbtiRef = useRef<HTMLInputElement>(null);
    const inspireSourceRef = useRef<HTMLInputElement>(null);
    const favoriteWorkTitleRef = useRef<HTMLInputElement>(null);
    const favoriteWorkReasonRef = useRef<HTMLInputElement>(null);
    const memorableEpisodeRef = useRef<HTMLTextAreaElement>(null);
    const futureWritingPlanRef = useRef<HTMLInputElement>(null);
    const happiestWritingMomentRef = useRef<HTMLInputElement>(null);

    const [errors, setErrors] = useState<FormErrors>({});
    const [profilePreview, setProfilePreview] = useState<string | Blob | undefined>(undefined);
    const fileInputProfileRef = useRef<HTMLInputElement | null>(null);
    const [showImageCrop, setShowImageCrop] = useState(false);
    const [image, setImage] = useState<string | null>(null);
    const [showAlert, setShowAlert] = useState(false);

    const keywords = [
        '재미와 감동을 주는', '전문 식견이 담긴', '소재가 신선한', '객관적 근거를 제시하는',
        '경험을 바탕으로 한', '극적 긴장감을 주는', '감수성이 돋보이는', '나만의 생각이 담긴',
        '표현력이 자유로운', '상상력이 돋보이는', '주장이 명확한', '논리적인 구조를 갖춘',
    ];
    const [selectedKeywords, setSelectedKeywords] = useState<string | null>(null);

    useEffect(() => {
        if (profileList) {
            setInstarLink(profileList.instagramLink || "");
            setTwitterLink(profileList.xlink || "");
            setBrunchLink(profileList.brunchLink || "");
            setBlogLink(profileList.naverLink || "");
            setSnsLink1(profileList.snsLink1 || "");
            setSnsLink2(profileList.snsLink2 || "");
            setAuthorIntro(profileList.authorIntro || "");
            setAuthorDescription(profileList.authorDescription || "");
            setImage(profileList.profilePhoto || "");
            setProfilePreview(profileList.profilePhoto || "");
            setSelectedKeywords(profileList.keyword1);
            setMbti(profileList.mbti || "");
            if (profileList.writingGenre) {
                setWritingGenres(profileList.writingGenre.split(', ').filter(Boolean));
            } else {
                setWritingGenres([]);
            }
            setInspireSource(profileList.inspirationSource || "");
            if (profileList.favoriteWorkAndQuote && profileList.favoriteWorkAndQuote.includes(':')) {
                const parts = profileList.favoriteWorkAndQuote.split(':');
                setFavoriteWorkTitle(parts[0].trim());
                setFavoriteWorkReason(parts.slice(1).join(':').trim());
            } else {
                setFavoriteWorkTitle(profileList.favoriteWorkAndQuote || "");
                setFavoriteWorkReason("");
            }
            setMemorableEpisode(profileList.behindCreationEpisode || "");
            setHappiestWritingMoment(profileList.happiestWritingMoment || "");
            setFutureWritingPlan(profileList.futureWritingPlan || "");
        }
    }, [profileList]);

    const toggleKeyword = (keyword: string) => {
        setSelectedKeywords(selectedKeywords === keyword ? null : keyword);
    };

    const openModal = () => setShowImageCrop(true);
    const closeModal = () => setShowImageCrop(false);

    const handleCrop = (cropData: string) => {
        setProfilePreview(cropData);
        closeModal();
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            setImage(reader.result as string);
            openModal();
        };
        reader.readAsDataURL(file);
    };

    const scrollToField = (el: HTMLElement | null) => {
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
            if ('focus' in el && typeof (el as any).focus === 'function') (el as any).focus();
        }, 300);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const jwtToken = localStorage.getItem('jwtToken');
        if (!jwtToken) { alert('로그인 상태를 확인해주세요.'); return; }

        const formData = new FormData();
        const email = localStorage.getItem("email");
        if (email) formData.append('authorEmail', email);

        const fieldsToValidate = [
            { label: '영감을 받는 대상', value: inspireSource, ref: inspireSourceRef },
            { label: '행복한 순간', value: happiestWritingMoment, ref: happiestWritingMomentRef },
            { label: '작품 선택 이유', value: favoriteWorkReason, ref: favoriteWorkReasonRef },
            { label: '에피소드', value: memorableEpisode, ref: memorableEpisodeRef },
            { label: '앞으로의 계획', value: futureWritingPlan, ref: futureWritingPlanRef },
        ];
        for (const field of fieldsToValidate) {
            const error = optionalCharError(field.value);
            if (error) { scrollToField(field.ref.current); return; }
        }

        formData.append('authorName', profileList.authorName);
        formData.append('instagramLink', instarLink);
        formData.append('xLink', twitterLink);
        formData.append('naverLink', blogLink);
        formData.append('brunchLink', brunchLink);
        formData.append('snsLink1', snsLink1);
        formData.append('snsLink2', snsLink2);
        formData.append('authorIntro', authorIntro);
        formData.append('authorDescription', authorDescription);
        if (selectedKeywords) formData.append('keyword1', selectedKeywords);
        formData.append('mbti', mbti);
        formData.append('writingGenre', writingGenres.join(', '));
        formData.append('inspirationSource', inspireSource);
        if (favoriteWorkTitle || favoriteWorkReason) {
            formData.append('favoriteWorkAndQuote', `${favoriteWorkTitle}: ${favoriteWorkReason}`);
        }
        formData.append('behindCreationEpisode', memorableEpisode);
        formData.append('happiestWritingMoment', happiestWritingMoment);
        formData.append('futureWritingPlan', futureWritingPlan);

        if (profilePreview) {
            let file;
            if (typeof profilePreview === 'string') {
                const base64Url = profilePreview.startsWith('data:image/')
                    ? profilePreview
                    : `data:image/png;base64,${profilePreview}`;
                const blob = base64ToBlob(base64Url, 'image/png');
                file = blobToFile(blob, 'profile.png');
            } else {
                file = blobToFile(profilePreview, 'profile.png');
            }
            formData.append('profilePhoto', file);
        }

        try {
            const response = await updateProfile(formData);
            setShowAlert(true);
        } catch (error) {
            const errorContent = error as any;
            const data = errorContent?.response?.data;
            const message: string =
                typeof data === 'string' ? data
                : data?.message ?? '';

            if (message.toLowerCase().includes('cwebp') || message.toLowerCase().includes('libwebp')) {
                alert('이미지 변환 처리 중 오류가 발생했습니다.\n다른 형식(JPG, PNG)의 이미지로 다시 시도해 주세요.');
            } else if (message) {
                alert(message);
            } else {
                alert('프로필 수정 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
            }
        }
    };

    const base64ToBlob = (base64: string, contentType: string): Blob => {
        const byteCharacters = atob(base64.split(',')[1]);
        const byteArrays = [];
        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
            const slice = byteCharacters.slice(offset, offset + 512);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) byteNumbers[i] = slice.charCodeAt(i);
            byteArrays.push(new Uint8Array(byteNumbers));
        }
        return new Blob(byteArrays, { type: contentType });
    };

    const blobToFile = (blob: Blob, fileName: string): File =>
        new File([blob], fileName, { type: blob.type });

    const getImageSrc = (data?: string | Blob): string | undefined => {
        if (!data) return undefined;
        if (typeof data === 'string') {
            return data.startsWith('data:image/') ? data : `data:image/png;base64,${data}`;
        }
        return URL.createObjectURL(data);
    };

    const extraFieldsProps = {
        mbti, setMbti,
        writingGenres, setWritingGenres,
        inspireSource, setInspireSource,
        favoriteWorkTitle, setFavoriteWorkTitle,
        favoriteWorkReason, setFavoriteWorkReason,
        memorableEpisode, setMemorableEpisode,
        futureWritingPlan, setFutureWritingPlan,
        happiestWritingMoment, setHappiestWritingMoment,
        mbtiRef, inspireSourceRef, favoriteWorkTitleRef,
        favoriteWorkReasonRef, memorableEpisodeRef,
        futureWritingPlanRef, happiestWritingMomentRef,
    };

    const snsFields = [
        { Icon: Instar, label: 'Instagram', value: instarLink, setter: setInstarLink, placeholder: 'https://instagram.com/yourname' },
        { Icon: Twitter, label: 'X (Twitter)', value: twitterLink, setter: setTwitterLink, placeholder: 'https://x.com/yourname' },
        { Icon: Brunch, label: 'Brunch', value: brunchLink, setter: setBrunchLink, placeholder: 'https://brunch.co.kr/@yourname' },
        { Icon: Blog, label: '블로그 / 홈페이지', value: blogLink, setter: setBlogLink, placeholder: 'https://your-blog.com' },
        { Icon: AddSns, label: '기타 SNS 1', value: snsLink1, setter: setSnsLink1, placeholder: 'https://' },
        { Icon: AddSns, label: '기타 SNS 2', value: snsLink2, setter: setSnsLink2, placeholder: 'https://' },
    ];

    return (
        <div css={indexWrapCss}>
            <Header onChange={onSearchHandler} searchValue={search} name="search" activeFlag="" />

            <div className="w-full max-w-[860px] mx-auto px-4 py-12 md:py-8 flex flex-col gap-8">

                {/* 페이지 헤더 */}
                <div className="flex flex-col gap-1">
                    <h1 className="text-[28px] md:text-[24px] font-bold text-[#111827]">프로필 수정</h1>
                    <p className="text-[14px] text-[#6b7280]">{profileList?.authorName} 작가님의 프로필을 수정하세요.</p>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col gap-6">

                    {/* ── 프로필 사진 ── */}
                    <SectionCard title="프로필 사진">
                        <div className="flex items-start gap-8 md:flex-col md:items-center md:gap-5">
                            {/* 프리뷰 */}
                            <div className="flex flex-col items-center gap-3 flex-shrink-0">
                                <div className="w-[140px] h-[140px] rounded-full overflow-hidden border-4 border-[rgba(25,207,122,0.25)] shadow-md">
                                    {profilePreview ? (
                                        <img
                                            className="w-full h-full object-cover"
                                            src={getImageSrc(profilePreview)}
                                            alt="Profile Preview"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-[#f3f4f6] flex items-center justify-center">
                                            <svg width="48" height="48" viewBox="0 0 24 24" fill="#d1d5db">
                                                <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
                                            </svg>
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="relative flex items-center gap-2 bg-[#19cf7a] hover:bg-[#00b368] text-white text-[14px] font-medium px-5 py-2.5 rounded-full transition-colors"
                                >
                                    <input
                                        ref={fileInputProfileRef}
                                        className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                    />
                                    <PlusIcon />
                                    <span>사진 업로드</span>
                                </button>
                            </div>

                            {/* 가이드 */}
                            <div className="flex-1 bg-[#f9fafb] rounded-xl p-5 text-[13px] text-[#6b7280] leading-relaxed md:w-full">
                                <p className="font-semibold text-[#374151] mb-2">📷 프로필 사진 업로드 안내</p>
                                <ul className="list-disc list-inside flex flex-col gap-1">
                                    <li>최소 400 × 400 px / 최대 800 × 1080 px</li>
                                    <li>최대 파일 크기: 1 MB</li>
                                    <li>본인 식별이 가능한 사진으로 등록해 주세요.</li>
                                    <li>선정적이거나 부적절한 사진은 반려될 수 있습니다.</li>
                                </ul>
                            </div>
                        </div>
                        {errors.profile && <p className="text-red-500 text-[13px]">{errors.profile}</p>}
                    </SectionCard>

                    {/* ── SNS 링크 ── */}
                    <SectionCard title="SNS 링크">
                        <div className="grid grid-cols-1 gap-4">
                            {snsFields.map(({ Icon, label, value, setter, placeholder }) => (
                                <div key={label} className="flex items-center gap-3">
                                    <div className="flex items-center justify-center w-10 h-10 bg-[#f9fafb] rounded-xl border border-[#f3f4f6] flex-shrink-0">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <span className="text-[12px] text-[#9ca3af] font-medium block mb-1">{label}</span>
                                        <input
                                            className={inputBaseCls}
                                            type="text"
                                            value={value}
                                            placeholder={placeholder}
                                            onChange={(e) => setter(e.target.value)}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </SectionCard>

                    {/* ── 작가 소개 ── */}
                    <SectionCard title="작가 소개">
                        <FieldLabel
                            label="작가님을 한 마디로 소개해 주세요."
                            sub="작가님을 한 마디로 나타낸다면 어떤 표현이 가장 잘 어울릴까요? (최대 150자)"
                        >
                            <div>
                                <textarea
                                    ref={introRef}
                                    className={`${textareaBaseCls} h-36`}
                                    value={authorIntro}
                                    maxLength={150}
                                    placeholder="예) 독자의 마음을 살며시 두드리는 글을 씁니다."
                                    onChange={(e) => setAuthorIntro(e.target.value)}
                                />
                                <div className="flex justify-between items-center mt-1">
                                    {errors.intro
                                        ? <p className="text-red-500 text-[12px]">{errors.intro}</p>
                                        : <span />
                                    }
                                    <p className="text-[12px] text-[#9ca3af] ml-auto">{authorIntro.length}/150</p>
                                </div>
                            </div>
                        </FieldLabel>

                        <FieldLabel
                            label="작가님에 대해 조금 더 자세히 들려주세요."
                            sub="독자들에게 작가님의 이야기를 더 자세히 소개해 주세요. (최대 500자)"
                        >
                            <div>
                                <textarea
                                    ref={descRef}
                                    className={`${textareaBaseCls} h-40`}
                                    value={authorDescription}
                                    maxLength={500}
                                    placeholder="작가님의 글 세계, 글쓰기 철학, 독자들에게 전하고 싶은 이야기 등을 자유롭게 적어주세요."
                                    onChange={(e) => setAuthorDescription(e.target.value)}
                                />
                                <div className="flex justify-between items-center mt-1">
                                    {errors.desc
                                        ? <p className="text-red-500 text-[12px]">{errors.desc}</p>
                                        : <span />
                                    }
                                    <p className="text-[12px] text-[#9ca3af] ml-auto">{authorDescription.length}/500</p>
                                </div>
                            </div>
                        </FieldLabel>
                    </SectionCard>

                    {/* ── 글 스타일 키워드 ── */}
                    <SectionCard title="글 스타일">
                        <FieldLabel
                            label="작가님은 주로 어떤 글을 쓰시나요?"
                            sub="가장 잘 어울리는 키워드 하나를 선택해 주세요."
                        >
                            <div className="grid grid-cols-3 md:grid-cols-2 gap-2">
                                {keywords.map((keyword) => {
                                    const isSelected = selectedKeywords === keyword;
                                    return (
                                        <button
                                            key={keyword}
                                            type="button"
                                            onClick={() => toggleKeyword(keyword)}
                                            className={`px-3 py-2.5 rounded-xl text-[14px] font-medium transition-all border ${
                                                isSelected
                                                    ? 'bg-[#19cf7a] border-[#19cf7a] text-white shadow-sm'
                                                    : 'bg-white border-[#e5e7eb] text-[#6b7280] hover:border-[#19cf7a] hover:text-[#19cf7a]'
                                            }`}
                                        >
                                            {keyword}
                                        </button>
                                    );
                                })}
                            </div>
                            {errors.keyword && <p className="text-red-500 text-[13px] mt-1">{errors.keyword}</p>}
                        </FieldLabel>
                    </SectionCard>

                    {/* ── 추가 정보 ── */}
                    <div className="bg-white rounded-2xl shadow-[0px_1px_4px_rgba(0,0,0,0.08)] border border-[#f3f4f6] px-8 py-8 md:px-5 md:py-6">
                        <ProfileExtraFields {...extraFieldsProps} />
                    </div>

                    {/* ── 이미지 크롭 모달 ── */}
                    <Modal
                        isOpen={showImageCrop}
                        shouldCloseOnOverlayClick={false}
                        onRequestClose={closeModal}
                        contentLabel="imageCrop Modal"
                        className="modal-login"
                        ariaHideApp={false}
                        overlayClassName="overlay"
                    >
                        {image && (
                            <ImageCrop
                                image={image}
                                onClose={closeModal}
                                onCrop={handleCrop}
                            />
                        )}
                    </Modal>

                    {/* ── 저장 버튼 ── */}
                    <div className="flex flex-col items-center gap-3 pt-2 pb-6">
                        <button
                            type="submit"
                            className="w-full max-w-[400px] h-[52px] bg-[#19cf7a] hover:bg-[#00b368] text-white text-[16px] font-bold rounded-2xl transition-colors shadow-[0px_4px_14px_rgba(25,207,122,0.3)]"
                        >
                            프로필 정보 수정
                        </button>
                        <button
                            type="button"
                            className="w-full max-w-[400px] h-[48px] bg-white border border-[#e5e7eb] text-[#6b7280] text-[15px] font-medium rounded-2xl hover:bg-[#f9fafb] transition-colors"
                            onClick={() => {
                                navigate(-1);
                                setTimeout(() => window.scrollTo(0, 0), 100);
                            }}
                        >
                            마이페이지로 돌아가기
                        </button>
                    </div>

                    {showAlert && (
                        <Alert
                            title=""
                            message="프로필 수정이 완료되었습니다."
                            onClose={() => { setShowAlert(false); navigate('/mypage'); }}
                            onConfirm={() => { setShowAlert(false); navigate('/mypage'); }}
                        />
                    )}
                </form>
            </div>

            <Footer />
        </div>
    );
};

export default ProfileEdit;
